import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { Flame, Loader2 } from "lucide-react";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2000);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8" data-testid="reset-password-logo">
          <span className="w-9 h-9 rounded-md bg-primary flex items-center justify-center"><Flame className="w-5 h-5 text-white" /></span>
          <span className="font-display text-xl font-bold">DealWisp</span>
        </Link>
        <div className="bg-card border border-white/10 rounded-2xl p-8">
          {!token ? (
            <p className="text-sm text-primary" data-testid="reset-password-no-token">
              This link is missing its token. Request a new one from the <Link to="/forgot-password" className="underline">reset page</Link>.
            </p>
          ) : done ? (
            <>
              <h1 className="font-display text-2xl font-bold tracking-tight">Password updated</h1>
              <p className="text-sm text-muted-foreground mt-2" data-testid="reset-password-done">Taking you to log in…</p>
            </>
          ) : (
            <>
              <h1 className="font-display text-2xl font-bold tracking-tight">Set a new password</h1>
              <p className="text-sm text-muted-foreground mt-1">Choose a new password for your account.</p>

              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <Label className="overline text-muted-foreground">New password</Label>
                  <Input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" data-testid="reset-password-input" className="mt-2 bg-background border-white/10" />
                </div>
                {error && <p className="text-sm text-primary" data-testid="reset-password-error">{error}</p>}
                <Button type="submit" disabled={loading} className="w-full rounded-full bg-primary hover:bg-secondary" data-testid="reset-password-submit">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
